import React from "react";
import { NavigateFunction } from "react-router-dom";
import { Button, Badge, Card, CardHeader, CardContent, CardTitle } from "@altimateai/lego";
import { Stack } from "@altimateai/lego";
import { Loader } from "../../components/Loader";
import { ActiveRun, EvalResult } from "../../services/evals";

interface ActiveRunCardProps {
  activeRun: ActiveRun;
  setSelectedResult: (result: EvalResult) => void;
  navigate: NavigateFunction;
}

export const ActiveRunCard: React.FC<ActiveRunCardProps> = ({
  activeRun,
  setSelectedResult,
  navigate,
}) => {
  return (
    <Card className="al-mb-6 al-border-2 al-border-blue-500">
      <CardHeader>
        <CardTitle>
          <Stack direction="column" gap="sm">
            <Stack direction="row" align="center" justify="between">
              <Stack direction="row" align="center" gap="lg">
                <span>{activeRun.name}</span>
                {activeRun.completed === activeRun.total ? (
                  <Badge className="!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-200">
                    Completed
                  </Badge>
                ) : (
                  <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
                    Active
                  </Badge>
                )}
              </Stack>
              <div className="al-text-sm al-font-normal al-text-gray-600">
                {activeRun.completed} / {activeRun.total} tests completed
              </div>
            </Stack>
            <div className="al-text-xs al-font-mono al-font-normal al-text-gray-500">
              Run ID: {activeRun.id}
            </div>
          </Stack>
        </CardTitle>
      </CardHeader>
      <CardContent>
        {/* Running Loader */}
        {activeRun.completed < activeRun.total && (
          <div className="al-mb-4">
            <Loader label="Running evaluations..." />
          </div>
        )}

        {/* Progress bar */}
        <div className="al-mb-6">
          <div className="al-w-full al-bg-gray-200 al-rounded-full al-h-2.5 al-overflow-hidden">
            <div
              className="al-bg-blue-600 al-h-2.5 al-rounded-full al-transition-all al-duration-300"
              style={{ width: `${Math.round((activeRun.completed / activeRun.total) * 100)}%` }}
            />
          </div>
          <div className="al-text-xs al-text-gray-500 al-text-center al-mt-2">
            {Math.round((activeRun.completed / activeRun.total) * 100)}% complete
          </div>
        </div>

        {/* Test-by-test breakdown */}
        {activeRun.testStatuses.length > 0 && (
          <div className="al-mb-6">
            <h3 className="al-text-sm al-font-semibold al-mb-3">Test Status</h3>
            <div className="al-border al-border-gray-200 al-rounded-md al-overflow-hidden">
              <table className="al-w-full al-divide-y al-divide-gray-200">
                <thead className="al-bg-gray-50">
                  <tr>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Test Name</th>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Status</th>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Score</th>
                  </tr>
                </thead>
                <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                  {activeRun.testStatuses.map((test, idx) => (
                    <tr key={idx} className="hover:al-bg-gray-50">
                      <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium">
                        {test.testCaseName}
                      </td>
                      <td className="al-px-4 al-py-3 al-text-[13px]">
                        {test.status === 'pending' && (
                          <Badge className="!al-bg-gray-100 !al-text-gray-700 al-border al-border-gray-200">
                            Pending
                          </Badge>
                        )}
                        {test.status === 'running' && (
                          <Badge className="!al-bg-blue-100 !al-text-blue-700 al-border al-border-blue-200">
                            Running...
                          </Badge>
                        )}
                        {test.status === 'passed' && (
                          <Badge className="!al-bg-green-100 !al-text-green-700 al-border al-border-green-200">
                            Passed
                          </Badge>
                        )}
                        {test.status === 'failed' && (
                          <Badge className="!al-bg-red-100 !al-text-red-700 al-border al-border-red-200">
                            Failed
                          </Badge>
                        )}
                      </td>
                      <td className="al-px-4 al-py-3 al-text-[13px]">
                        {test.score !== undefined ? (
                          <Badge
                            className={
                              Math.round(test.score) >= 4
                                ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                                : Math.round(test.score) >= 3
                                ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                                : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                            }
                          >
                            {Math.round(test.score)}/5
                          </Badge>
                        ) : (
                          <span className="al-text-gray-400">-</span>
                        )}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}

        {/* Summary stats */}
        {activeRun.completed === activeRun.total && (
          <div className="al-grid al-grid-cols-3 al-gap-4 al-mb-6">
            <div className="al-text-center al-p-4 al-bg-gray-50 al-rounded-lg">
              <div className="al-text-2xl al-font-bold">{activeRun.total}</div>
              <div className="al-text-sm al-text-gray-600">Total Tests</div>
            </div>
            <div className="al-text-center al-p-4 al-bg-green-50 al-rounded-lg">
              <div className="al-text-2xl al-font-bold al-text-green-600">{activeRun.successful}</div>
              <div className="al-text-sm al-text-gray-600">Successful</div>
            </div>
            <div className="al-text-center al-p-4 al-bg-red-50 al-rounded-lg">
              <div className="al-text-2xl al-font-bold al-text-red-600">{activeRun.failed}</div>
              <div className="al-text-sm al-text-gray-600">Failed</div>
            </div>
          </div>
        )}

        {/* Results list */}
        {activeRun.results.length > 0 && (
          <div>
            <h3 className="al-text-sm al-font-semibold al-mb-3">Results</h3>
            <div className="al-border al-border-gray-200 al-rounded-md al-overflow-hidden">
              <table className="al-w-full al-divide-y al-divide-gray-200">
                <thead className="al-bg-gray-50">
                  <tr>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Test Name</th>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Score</th>
                    <th className="al-px-4 al-py-3 al-text-left al-text-[13px] al-font-normal al-text-gray-500">Actions</th>
                  </tr>
                </thead>
                <tbody className="al-bg-white al-divide-y al-divide-gray-200">
                  {activeRun.results.map((result) => (
                    <tr key={result.id} className="hover:al-bg-gray-50">
                      <td className="al-px-4 al-py-3 al-text-[13px] al-text-gray-900 al-font-medium">
                        {result.test_case_name}
                      </td>
                      <td className="al-px-4 al-py-3 al-text-[13px]">
                        <Badge
                          className={
                            Math.round(result.overall_score) >= 4
                              ? "!al-bg-green-100 !al-text-green-700 al-border al-border-green-200"
                              : Math.round(result.overall_score) >= 3
                              ? "!al-bg-yellow-100 !al-text-yellow-700 al-border al-border-yellow-200"
                              : "!al-bg-red-100 !al-text-red-700 al-border al-border-red-200"
                          }
                        >
                          {Math.round(result.overall_score)}/5
                        </Badge>
                      </td>
                      <td className="al-px-4 al-py-3 al-text-[13px]">
                        <Button
                          onClick={() => {
                            setSelectedResult(result);
                            navigate(`?tab=runs&result_id=${result.id}`, { replace: true });
                          }}
                          size="sm"
                          variant="outline"
                        >
                          View Details
                        </Button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
};
